"use client";
import React, { useEffect, useRef, useState } from "react";
import IndustryHeader from "../../app/solution-landing-page/IndustryHeader";
import { Images, Icons } from "@/app/assets/shared";
import Image, { StaticImageData } from "next/image";
import { TabItem } from "@/lib/Types";

interface TabArrayAttributes {
  title: string;
  content: string;
  icon?: string | StaticImageData;
}

interface HeadingProps {
  title: React.ReactNode;
  subtitle: string;
  buttonText: string;
}

interface WhyChooseIndustryProps {
  tabArray: TabArrayAttributes[];
  heading: HeadingProps;
  desc: string;
  ctaButton: React.ReactNode;
}

export default function WhyChooseIndustry({
  tabArray,
  heading,
  desc,
  ctaButton,
}: WhyChooseIndustryProps) {
  const sectionRef1 = useRef<HTMLDivElement | null>(null);

  const [isSplineLoaded, setIsSplineLoaded] = useState(false);
  const [hasSplineLoadedOnce, setHasSplineLoadedOnce] = useState(false);
  const [inView, setInView] = useState(false);
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 1024);
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);
  useEffect(() => {
    if (tabArray && tabArray.length > 0 && active >= tabArray.length) {
      setActive(0);
    }
  }, [tabArray, active]);

  const splitTitle = (title: string) => {
    const parts = title.trim().split(" ");
    if (parts.length === 1) return { first: title, last: "" };
    const last = parts.pop() as string;
    return { first: parts.join(" "), last };
  };

  // Guard against undefined or empty tabArray
  const safeTabArray = tabArray || [];
  const hasTabs = safeTabArray.length > 0;
  const activeIndex = hasTabs && active < safeTabArray.length ? active : 0;
  const activeTab = safeTabArray[activeIndex];
  const activeParts = activeTab
    ? splitTitle(activeTab.title)
    : { first: "", last: "" };

  // Observe section in-view like Technology section
  useEffect(() => {
    const target = sectionRef1.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.2 },
    );

    observer.observe(target);

    // Cleanup uses the same element reference captured above
    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, []);

  // Load Spline once when first in view
  useEffect(() => {
    if (inView && !hasSplineLoadedOnce) {
      setIsSplineLoaded(true);
      setHasSplineLoadedOnce(true);
    }
  }, [inView, hasSplineLoadedOnce]);
  return (
    <section
      id="why-choose-industry-2"
      className="why-choose-industry overflow-hidden bg-[#000000] relative pb-[30px] lg:pb-[150px] w-full"
      ref={sectionRef1}
    >
      <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
        {" "}
        <div className="absolute inset-0 z-0">
          <Image
            src={Images?.texture}
            alt="texture"
            fill
            className="object-cover mix-blend-overlay opacity-60"
            loading="lazy"
            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
          />
        </div>
      </div>
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px]  absolute left-0 bottom-0  -translate-x-1/3 translate-y-1/3" />

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[40px] lg:pt-[150px] relative z-1">
        <IndustryHeader
          title={heading?.title}
          subtitle={heading?.subtitle}
          description={desc}
          button={ctaButton}
        />

        {/* Tabs + Content */}
        {!hasTabs ? null : isMobile ? (
          <div className="mt-[30px] lg:mt-[60px] grid grid-cols-1 gap-[20px]">
            {safeTabArray?.map((tab: TabItem, idx: number) => {
              const parts = splitTitle(tab.title);
              return (
                <div
                  key={`mobile-tab-${tab.title}-${idx}`}
                  className="relative rounded-[10px] bg-white p-[30px] md:p-[40px] flex overflow-hidden gap-[20px]"
                >
                  <Image
                    src={(tab?.icon as string) || Icons?.security}
                    alt=""
                    className="hidden lg:block select-none pointer-events-none "
                    width={50}
                    height={50}
                    loading="lazy"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                  <div className="relative z-10 w-full grid grid-cols-1 gap-[12px]">
                    <h3 className="font-medium clash-font text-[length:var(--tab-content-title)] leading-[1.1] text-[#00040b]">
                      {parts.first}
                      {parts.last && (
                        <span className="block text-[#225BD4]">
                          {parts.last}
                        </span>
                      )}
                    </h3>
                    <p
                      className="text-[length:var(--primarybtn)] leading-[1.6] text-[#636364]"
                      dangerouslySetInnerHTML={{ __html: tab.content ?? "" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-[30px] lg:mt-[60px] grid grid-cols-1 lg:grid-cols-12 gap-[45px]">
            {/* Left list */}
            <div className="lg:col-span-4 bg-transparent h-full">
              <div className="flex flex-col">
                {safeTabArray?.map((tab: any, idx: number) => (
                  <div key={`desktop-tab-${tab.title}-${idx}`}>
                    <button
                      type="button"
                      aria-label={`Learn more about ${Array.isArray(tab.title) ? tab.title.join(" ") : tab.title}`}
                      onClick={() => setActive(idx)}
                      className={`w-full flex items-center cursor-pointer justify-between py-[20px] transition-colors ${
                        active === idx ? "opacity-100" : "opacity-40"
                      }`}
                    >
                      <span
                        className={`text-[length:var(--why-choose-tab-link)] clash-font text-left ${
                          active === idx
                            ? "text-white font-medium"
                            : "text-white font-normal"
                        }`}
                      >
                        {tab.title}
                      </span>
                      <Image
                        src={Icons?.chevronWhiteLeft}
                        alt=""
                        width={12}
                        height={12}
                        className="w-3 h-3 shrink-0 opacity-60"
                        loading="lazy"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </button>
                    {/* Divider */}
                    {idx !== safeTabArray.length - 1 && (
                      <div className="h-px w-full bg-[#636364]/50" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-8 h-full">
              <div
                className="relative h-full bg-white p-[30px]
                         md:p-[40px] lg:p-[50px] flex overflow-hidden gap-[40px]"
              >
                {/* Watermark icon */}
                <div className="hidden xl:flex flex-1 items-center justify-center p-[40px]">
                  {activeTab?.icon && (
                    <Image
                      src={activeTab.icon} // now TS knows it's defined
                      alt=""
                      width={156}
                      height={156}
                      loading="lazy"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  )}
                </div>
                <div className="relative flex-[3] z-10 place-content-center w-full grid grid-cols-1 gap-[20px]">
                  <h3
                    className="font-medium clash-font text-[length:var(--tab-content-title)] leading-[1.1] text-[#00040b]"
                    onCopy={(e) => {
                      const text =
                        `${activeParts.first} ${activeParts.last || ""}`.trim();

                      e.preventDefault();
                      e.clipboardData.setData("text/plain", text);
                    }}
                  >
                    {activeParts.first}
                    {activeParts.last && (
                      <span className="block text-[#225BD4]">
                        {activeParts.last}
                      </span>
                    )}
                  </h3>
                  <p
                    className="text-[length:var(--primarybtn)] leading-[1.6] text-[#636364] max-w-[680px]"
                    dangerouslySetInnerHTML={{
                      __html: activeTab?.content ?? "",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {inView && (
        <div
          className={`${
            isSplineLoaded ? "block" : "hidden"
          } w-full aspect-[1/1] absolute bottom-0 right-0 max-w-[400px]`}
        ></div>
      )}
    </section>
  );
}
